import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { NotificationBell, type AppNotification } from './NotificationBell';
import { useStoredNotifications } from '../features/notifications/useStoredNotifications';
import { timeAgo } from '../features/check-batch/format';
import { useOpenEmployeeQuestionCount } from '../features/people/useOpenEmployeeQuestionCount';
import { StagingBanner } from './StagingBanner';
import { SideNav } from './SideNav';
import { ProfileMenu } from '../shared/ProfileMenu';
import { BusinessViewSwitcher } from '../shared/BusinessViewSwitcher';
import { ThemeToggle } from '../shared/theme/ThemeToggle';
import { TrialUpgradeBadge } from '../features/subscription/TrialUpgradeBadge';
import { SubscriptionBanner } from '../features/subscription/SubscriptionBanner';
import { SubscriptionRestrictionNotice } from '../features/subscription/SubscriptionRestrictionNotice';
import escochexLogo from '../assets/escochex-logo.webp';
import { AutoTaxIcon } from './navIcons';

export function AppShell() {
  // The one read of the waiting-question count, shared by the bell and the side menu.
  const openQuestions = useOpenEmployeeQuestionCount();
  // A new page opens at its top. Without this the scroll position of the previous page (for example after reaching a link low in the
  // side menu) is kept, and the new page's title and filters open scrolled out of view.
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  // EPN-231: stored notifications (Identity) join the items derived from live state. A stored item stays listed once read (dimmed); only
  // unread ones count toward the bell. The path comes from the server, so only an in-app path is ever linked.
  const stored = useStoredNotifications(pathname);
  const derived: AppNotification[] = openQuestions > 0
    ? [{
        id: 'employee-questions',
        title: `${openQuestions} employee ${openQuestions === 1 ? 'question' : 'questions'} waiting`,
        detail: 'Employees asked about details you set for them.',
        to: '/people/employee-questions',
      }]
    : [];
  const storedItems: AppNotification[] = stored.items.map((n) => ({
    id: n.id,
    title: n.title,
    detail: n.detail,
    to: n.targetPath.startsWith('/') && !n.targetPath.startsWith('//') ? n.targetPath : '/',
    unread: !n.isRead,
    time: timeAgo(n.createdAtUtc),
    onOpen: n.isRead ? undefined : () => stored.markRead(n.id),
  }));
  const notifications = [...storedItems, ...derived];
  const bellCount = openQuestions + stored.unreadCount;
  return (
    <div className="page app-shell">
      <StagingBanner />
      <header className="topbar">
        <div className="topbar-brand">
          <img src={escochexLogo} alt="EscoChex — A Payroll Software Company" className="brand-logo brand-logo--topbar" />
          <BusinessViewSwitcher />
        </div>
        <div className="topbar-actions">
          <TrialUpgradeBadge />
          {/* EPN-46 — placement per the approved User Account Dropdown reference (theme toggle sits
              immediately left of the account trigger). ThemeToggle itself was already built (for the
              Sign In flow) but had no confirmed placement anywhere else — reused here, not rebuilt. */}
          <ThemeToggle />
          <NotificationBell
            notifications={notifications}
            count={bellCount}
            onMarkAllRead={stored.unreadCount > 0 ? stored.markAllRead : undefined}
          />
          <ProfileMenu />
        </div>
      </header>
      {/* EPN-105 — one site-wide subscription banner, driven only by the server's subscription fields. Outside
          <main> on purpose: it is page chrome, not page content. */}
      <SubscriptionBanner />
      <div className="shell-body">
        <nav className="side-nav">
          {/* Shown in every approved Figma mockup, directly under the logo. No backend feature exists yet
              to actually enable/disable automatic tax remittance (grepped the whole repo — nothing), and
              Payroll Taxes is itself still "Shadow Tax Calculation — Not Approved for Production Payroll"
              (see moduleReadiness.ts). Rendering a live-looking ON/OFF control with no real effect would
              misrepresent payroll-tax behavior, so this is disabled with an honest "Coming soon" label
              instead — same treatment DemoFeaturePage already uses for a not-yet-built capability. */}
          <div className="auto-tax-payment-toggle" aria-disabled="true">
            <AutoTaxIcon />
            <span>Auto Tax Payment</span>
            <span className="entitlement-badge entitlement-badge--coming-soon" role="status">
              <span aria-hidden="true">◷</span> Coming soon
            </span>
          </div>
          <SideNav openQuestionCount={openQuestions} />
        </nav>
        <main className="content">
          {/* EPN-105 — global handling of a server 403 SUBSCRIPTION_* refusal from any API call. */}
          <SubscriptionRestrictionNotice />
          <Outlet />
        </main>
      </div>
    </div>
  );
}
